<template>
  <div class="edit-permission">
    <div class="edit-permission__title">
      Edit Permission
    </div>
    <label class="edit-permission__input">
      <span class="edit-permission__label">
        Category
      </span>
      <div
        class="edit-permission__link-wrapper"
        :class="{'edit-permission__link-wrapper_single': isDomainDropdownHidden}"
      >
        <base-select
          v-model="permission.category_id"
          class="edit-permission__active"
          :disabled="disableButtons"
          :options="categories"
          :max-height-list="450"
          :title-style="titleStyle"
          :header-style="selectTypeStyle"
        />
      </div>
    </label>
    <label class="edit-permission__input">
      <span class="edit-permission__label">
        Resource
      </span>
      <div
        class="edit-permission__link-wrapper"
        :class="{'edit-permission__link-wrapper_single': isDomainDropdownHidden}"
      >
        <input
          v-model="permission.resource"
          type="text"
          :disabled="disableButtons"
          class="form-control edit-permission__link-field"
          placeholder="Enter the Resource for this permission"
          :class="{'form-control_error': externalErrors.name}"
        >
        <span
          v-if="externalErrors.resource"
          class="edit-permission__error-message"
        >
          {{ externalErrors.resource }}
        </span>
      </div>
    </label>
    <label class="edit-permission__input">
      <span class="edit-permission__label">
        Name
      </span>
      <div
        class="edit-permission__link-wrapper"
        :class="{'edit-permission__link-wrapper_single': isDomainDropdownHidden}"
      >
        <input
          v-model="permission.name"
          type="text"
          :disabled="disableButtons"
          class="form-control edit-permission__link-field"
          placeholder="Enter the Name for this permission"
          :class="{'form-control_error': externalErrors.name}"
        >
        <span
          v-if="externalErrors.name"
          class="edit-permission__error-message"
        >
          {{ externalErrors.name }}
        </span>
      </div>
    </label>
    <label class="edit-permission__input">
      <span class="edit-permission__label">
        Description
      </span>
      <div
        class="form-control description__wrapper"
        :class="{'description__wrapper_disabled': disableButtons}"
      >
        <textarea
          v-model="permission.description"
          class="description"
          :disabled="disableButtons"
          rows="6"
          placeholder="Enter Description"
        />
      </div>
    </label>

    <div class="edit-permission__associations_container">
      <div class="edit-permission__tabs">
        <span
          class="edit-permission__tabs_tab"
          :class="{'edit-permission__tabs_tab_active': selectedTab === 'groups' }"
          @click="selectedTab = 'groups'"
        >
          Groups
        </span>
        <span
          class="edit-permission__tabs_tab"
          :class="{'edit-permission__tabs_tab_active': selectedTab === 'users' }"
          @click="selectedTab = 'users'"
        >
          Users
        </span>
      </div>
      <div class="edit-permission__content-wrapper">
        <div>
          <div class="check-row">
            <div class="checkbox-label checkbox-label__label">
              <label>
                Name
              </label>
            </div>
            <div class="checkbox-container checkbox-container__label">
              <label>
                Allow
              </label>
              <label>
                Deny
              </label>
            </div>
          </div>
        </div>
        <template v-if="selectedTab === 'users'">
          <div
            v-for="(user, i) in users"
            :key="i"
            class="check-row"
          >
            <label class="checkbox-label">
              {{ user.name }}
            </label>
            <div class="checkbox-container">
              <input
                v-model="permission.associated_users[user._id]"
                :false-value="null"
                type="checkbox"
                class="edit-permission__checkbox"
                @change="onPermissionChange(permission.associated_users, user._id)"
              >
              <input
                v-model="permission.associated_users[user._id]"
                type="checkbox"
                :true-value="false"
                :false-value="null"
                class="edit-permission__checkbox"
                @change="onPermissionChange(permission.associated_users, user._id)"
              >
            </div>
          </div>
        </template>
        <template v-else>
          <div
            v-for="(group, i) in groups"
            :key="i"
            class="check-row"
          >
            <label class="checkbox-label">
              {{ group.name }}
            </label>
            <div class="checkbox-container">
              <input
                v-model="permission.associated_groups[group.id]"
                :false-value="null"
                type="checkbox"
                class="edit-permission__checkbox"
                @change="onPermissionChange(permission.associated_groups, group.id)"
              >
              <input
                v-model="permission.associated_groups[group.id]"
                type="checkbox"
                :true-value="false"
                :false-value="null"
                class="edit-permission__checkbox"
                @change="onPermissionChange(permission.associated_groups, group.id)"
              >
            </div>
          </div>
        </template>
      </div>
    </div>
    <div class="edit-permission__controls">
      <button
        class="edit-permission__button edit-permission__button_cancel"
        :class="{'create-section__button_disabled': disableButtons}"
        @click.prevent="close()"
      >
        Cancel
      </button>
      <button
        class="edit-permission__button edit-permission__button_submit"
        :class="{'create-section__button_disabled': disableButtons}"
        @click.prevent="apply()"
      >
        Apply
      </button>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import _ from 'lodash'
import baseModalProps from '@/common/BaseModalProps'
import BaseModalMixins from '@/common/BaseModalMixins'

const SELECT_STYLE = {
  'background-color': 'transparent',
  color: '#284aff',
  'text-decoration': 'underline'
}

const ACTIVE_OPTIONS = [
  {
    label: 'Yes',
    value: true
  },
  {
    label: 'No',
    value: false
  }
]
export default {
  name: 'PermissionsCrud',
  mixins: [BaseModalMixins],
  props: {
    selectedPermission: {
      type: Object,
      required: false,
      default() {
        return {
          id: null,
          is_active: null,
          category_id: null,
          name: null,
          resource: null,
          description: null,
          associated_users: {},
          associated_groups: {},
        }
      }
    },
    categories: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      permission: _.cloneDeep(this.selectedPermission),
      externalErrors: {
        name: null,
        fk_pivot_table: null
      },
      disableButtons: false,
      isValidating: false,
      isDomainDropdownHidden: true,
      elementStyle: {},
      titleStyle: {
        overflow: 'visible'
      },
      activeOpts: _.cloneDeep(ACTIVE_OPTIONS),
      selectTypeStyle: _.cloneDeep(SELECT_STYLE),
      selectedTab: 'groups'
    }
  },
  computed: {
    tabOptions() {
      return {
        users: this.users,
        group: this.groups
      }
    },
    modalProps() {
      return { ...baseModalProps, height: 'auto', width: '510px' }
    },
    ...mapState('authorization', ['groups']),
    ...mapState('users', ['users']),
  },
  methods: {
    onPermissionChange(permissionObj, id) {
      if (permissionObj[id] === null) {
        delete permissionObj[id]
      }
    },
    close() {
      this.$emit('close')
    },
    convertToOptions(item) {
      return {
        value: item._id,
        label: item.label
      }
    },
    showErrors(errors) {
      for (const k in this.externalErrors) {
        this.externalErrors[k] = null
      }

      for (const k in errors) {
        this.externalErrors[k] = errors[k]
      }
    },
    async apply() {
      this.disableButtons = true
      const permission = {
        id: this.permission.id,
        associated_users: this.permission.associated_users,
        associated_groups: this.permission.associated_groups,
      }
      this.disableButtons = false
      this.$emit('update', permission)
    }
  }
}
</script>

<style lang="scss">
.checkbox-label {

  // justify-content: space-between;
  // width: fit-content;
  // flex
  &__label {
    font-weight: bold;
  }
}

.checkbox-container {
  display: flex;
  justify-content: space-between;
  width: 20%;

  // width: fit-content;
  // flex
  &__label {
    font-weight: bold;
  }
}

.check-row {
  margin-top: 10px;
  display: flex;
  justify-content: space-between;

}

.edit-permission {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 40px;

  &__checkbox {
    margin-top: 10px;
  }


  &__title {
    text-align: center;
    font-size: 34px;
    font-weight: 500;
    margin: 9px 0 35px;
    color: $rfx-dark-march;
  }

  &__input {
    display: flex;
    flex-direction: column;
    position: relative;
    margin-top: 30px;

    &_invalid[type='text'] {
      border: 1px solid $rfx-red;
      border-radius: 4px;
    }
  }

  &__associations_container {
    margin-top: 30px;
    // height: 500px;
  }

  &__content-wrapper {
    display: flex;
    flex-direction: column;
    padding: 10px 40px;
    background-color: $rfx-background-color;
    min-height: 300px;
  }

  &__tabs {
    display: flex;
    margin-bottom: -1px;

    &_btn {
      margin-left: auto;
    }

    &_tab {
      cursor: pointer;
      width: max-content;
      padding: 16px 9px;
      text-align: center;
      font-size: 14px;
      color: $rfx-light-march;
      background-color: transparent;
      border: 1px solid transparent;
      text-decoration: none;

      &_error {
        color: $rfx-red;
      }

      &_active {
        background-color: $rfx-background-color;
        color: $rfx-dark-march;
        font-weight: bold;
        border: 1px solid $rfx-light-steel;
        border-bottom: 1px solid $rfx-background-color;
      }
    }
  }

  &__label {
    display: block;
    font-weight: 600;
    margin-bottom: 5px;
    color: $rfx-dark-march;
  }

  &__controls {
    display: flex;
    justify-content: flex-end;
    margin-top: 30px;
  }

  &__button {
    width: 100px;
    height: 40px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    margin-left: 30px;
    border: none;
    outline: none;
    cursor: pointer;

    &_submit {
      background-color: $rfx-medium-march;
      color: #fff;

      &:disabled {
        pointer-events: none;
        background-color: #fff;
        color: $rfx-neutral-grey;
        cursor: default;
      }
    }

    &_disabled {
      pointer-events: none;
      opacity: 0.4;
      cursor: default;
    }

    &_delete {
      background-color: $rfx-red;
      color: #fff
    }

    &_cancel {
      color: $rfx-dark-march;
      background-color: #fff;
    }
  }

  &__link {
    &-wrapper {
      position: relative;
      margin-top: 30px;

      &_single {
        margin-top: 0;
      }
    }

    &-field[type='text'] {
      width: 100%;
      box-sizing: border-box;
      padding-right: 60px;
      font-weight: 500;
      @include ellipsis;
    }
  }

  &__error-message {
    color: $rfx-red;
    font-size: 10px;
    margin-top: 5px;
  }

}

.form-control {
  height: 50px;
  padding: 0 20px;
  border: 2px solid $rfx-light-steel;
  outline: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  color: $rfx-dark-march;

  &::placeholder {
    color: $rfx-pearl;
    font-weight: normal;
    font-size: 14px;
  }

  &:disabled {
    background-color: $rfx-light-steel;
  }

  &_error {
    border-color: $rfx-red;
    color: $rfx-red;

    &::placeholder {
      color: $rfx-red;
      font-size: 13px;
    }

  }
}

.description {
  width: 100%;
  height: 90%;
  padding: 0;
  resize: none;
  outline: none;
  border: none;
  font-size: 14px;

  &:disabled {
    background-color: transparent;
  }

  &::placeholder {
    color: $rfx-pearl;
    font-weight: normal;
    font-size: 14px;
    margin-top: -5px;
  }

  &__wrapper {
    padding: 10px 20px;
    height: auto;

    &_disabled {
      background-color: #fafafa;
    }
  }
}
</style>
